/* ═══════════════════════════════════════════════════════════════
   Spec MODULES — the shared brain (docs/MASTERS_UNIFIED_PLAN.md Phase 4).

   ⛔ GLOSSARY (Josh, binding): a MODULE is a reusable block-group,
   single-spec only. It is NOT a variant (a variant is a client version of a
   master). Never swap the two words.

   A module is a NAMED, FIRM-WIDE definition captured out of one spec:
   the selected blocks, grouped into contiguous chunks, each chunk carrying
   the placement ANCHOR (the Part/article path it was standing under) that
   Phase 5 will match against a target outline.

   ⛔ STAMPED COPY LAW (v2.12 grave): a module never references the blocks it
   was cut from. Every block is DEEP-COPIED with a FRESH id at save time;
   `sourceBlockId` rides along for provenance only and is never resolved.
   Per-spec runtime state (revision, comments, review comments, the system
   tags that only mean something inside the source spec) is stripped, so a
   module can be dropped into any spec later without dragging a foreign
   document's bookkeeping with it.

   Numbering/anchors come from the ONE numbering brain
   (shared/body-layout.ts → computeNumbering), never a second table.
   ═══════════════════════════════════════════════════════════════ */

import type { ContentBlock } from './spec';
import type { NumberedRef } from './body-layout';
import { hashBlock } from './spec-compose';

// ─── Types ───

/** A block stored INSIDE a module. Identical to a ContentBlock plus a
 *  provenance breadcrumb — deliberately additive so nothing downstream that
 *  eats a ContentBlock has to learn a new shape. */
export type ModuleBlock = ContentBlock & {
  /** The id this copy was cut from. PROVENANCE ONLY — never resolved, never
   *  used to look anything up (a module is a stamped copy, not a link). */
  sourceBlockId?: string;
  /** BUILDER-ONLY (R2 Phase 4). The part (1/2/3) a level-2 article declares
   *  while it is being edited. It is NEVER stored in a saved chunk — the
   *  saved truth is `anchor.partIndex`, and the builder re-hydrates this
   *  from it on open. Rides in the node's `meta` like every unmodelled
   *  field, so a round trip through the editor keeps it. */
  modulePart?: number;
  /** Builder state only (TWEAKS item 10): a PARAGRAPH dropped into a part
   *  that has no article yet. It has no article above it, so it is flagged
   *  until one arrives, and it is never saved. */
  modulePin?: number;
};

/** One rung of a chunk's ancestor chain, as it read in the source spec. */
export interface ModuleAnchorStep {
  /** 1-9. Level 1 IS the Part boundary. */
  level: number;
  /** The number the source spec printed for that ancestor ("PART 2 -", "2.3"). */
  prefix: string;
  /** The ancestor's text, trimmed and capped — what a human recognises. */
  headingText: string;
}

/** Where a chunk was standing when it was captured. Phase 5 matches this
 *  against the target outline; Phase 4 only records and displays it. */
export interface ModuleAnchor {
  /** The level of the chunk's FIRST block. */
  level: number;
  /** Ancestor chain, Part first → nearest ancestor last. Empty when the
   *  chunk starts at level 1 (a Part has no ancestors). */
  path: ModuleAnchorStep[];
  /** 1-based index of the PART the chunk lives in (0 = before any Part). */
  partIndex: number;
}

/** One contiguous run of captured blocks. */
export interface ModuleChunk {
  blocks: ModuleBlock[];
  anchor: ModuleAnchor;
}

/** One row of `module_tags` (migration 060) — the firm's tag catalog. A module
 *  still carries its tags as text in `module_library.tags`. */
/** Camel-case, like every other cache row (cloudCache maps firm_id → firmId). */
export interface ModuleTag {
  id: string;
  firmId?: string;
  name: string;
  createdAt?: string;
}

/** A firm-wide module definition — one row of `module_library`. */
export interface SpecModule {
  id: string;
  firmId?: string;
  name: string;
  description?: string;
  chunks: ModuleChunk[];
  /** Bumped when the DEFINITION changes. Phase 6 PRs read this. METADATA
   *  ONLY (migration 056 — `categoryId`/`tags`) never bumps this: see
   *  `shared/module-categories.ts` and ec2-api/server.js
   *  `shouldSnapshotModuleUpdate`. */
  version: number;
  /** The spec master this was captured from (provenance; may be deleted). */
  sourceSpecId?: string;
  /** That master's CSI number at capture time, for display. */
  sourceCsi?: string;
  /** Set ⇒ archived. Null/absent ⇒ live. (Archive and restore are one toggle.) */
  archivedAt?: string | null;
  createdBy?: string;
  createdAt?: string;
  modifiedBy?: string;
  lastModified?: string;
  /** Migration 056 — one category per module, nullable (uncategorized is
   *  valid). A dangling id (the category was deleted some other way than
   *  through the server's reparenting delete) reads as uncategorized
   *  everywhere — see `moduleEffectiveCategoryId` in module-categories.ts,
   *  never a crash. */
  categoryId?: string | null;
  /** Migration 056 — free-text tags, trimmed/de-duplicated server- and
   *  client-side by the SAME rule (`normalizeTags`). Absent/undefined on a
   *  module that predates this field — never sent as `[]` just to round-trip
   *  a save, since an older server may not have migration 056 yet. */
  tags?: string[];
  /** Sync D14 — the server-held DRAFT of the definition (null/absent = no
   *  draft, the module is Published). Inserts and Add to Project never read
   *  this; they read `chunks`. */
  draftChunks?: ModuleChunk[] | null;
  /** Server-derived (Sync D6): true while a draft waits to be published.
   *  Rides every list read and live-sync message; the Modules list status. */
  draftPending?: boolean;
  /** When / by whom the current `chunks` were published. */
  publishedAt?: string | null;
  publishedBy?: string | null;
}

/**
 * ONE archived module save (Cohesion R1 Job B1, migration 049). Written
 * server-side on every module create/definition-update — never by the
 * client directly — so the history can never fall behind what
 * `module_library` actually holds. Doubles as the module's edit-history
 * feed (every row IS an edit event); no second storage mechanism exists
 * for that.
 */
export interface ModuleVersion {
  id: string;
  firmId?: string;
  moduleId: string;
  version: number;
  name?: string;
  chunks: ModuleChunk[];
  message?: string | null;
  createdBy?: string;
  createdAt?: string;
}

// ─── Block copying ───

/** Keys that only mean something inside the spec a block was cut from.
 *  Dropped on the way into a module so an insert can never resurrect another
 *  document's review state or system tagging. */
const RUNTIME_KEYS = [
  'revision', // transient, recomputed at render/export
  'comments', // review collaboration on the SOURCE spec
  'included', // per-spec inclusion toggle
  'excluded', // Skip at export (Cohesion R1 Phase 1) — a module has no export
  // of its own, and nothing offers this toggle inside the builder; stripping
  // it here means a block excluded before it was captured (or one dragged in
  // from an old insertion) can never silently ride a skip flag into a spec.
  'systemId', // system membership belongs to the source spec
  'systemName',
  'confidence', // AI-import bookkeeping
  'source',
] as const;

/* `groupId` is deliberately NOT a runtime key (modules-polish, item 1): a
   module saved from grouped blocks KEEPS that grouping, and every insert
   re-mints it through one `createGroupRemap` per insert
   (`remapInsertGroups`), so two inserts of one module are two independent
   groups and no source-document id ever lands in a destination. Grouping is
   STRUCTURE, never content — `hashBlock` (spec-compose CONTENT_KEYS) does not
   read it, so it can never make an insertion read "modified here", never
   make a definition diff non-empty, and never raise a module-update PR. */

/** The id mint every copy here uses unless a test injects its own. */
const defaultMintId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `mb-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

/** Deep, JSON-safe copy. ContentBlock is plain data (text, runs, tableData,
 *  base64 image strings) so structuredClone-by-JSON is exact and cheap. */
function deepCopy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** One block → its module copy: deep, fresh id, runtime state stripped. */
export function copyBlockForModule(block: ContentBlock, newId: string): ModuleBlock {
  const copy = deepCopy(block) as unknown as Record<string, unknown>;
  for (const key of RUNTIME_KEYS) delete copy[key];
  const out = copy as unknown as ModuleBlock;
  out.id = newId;
  out.sourceBlockId = block.id;
  return out;
}

// ─── Anchors ───

const MAX_HEADING_CHARS = 120;

function headingTextOf(block: ContentBlock): string {
  const raw = (block.text || '').replace(/\s+/g, ' ').trim();
  return raw.length > MAX_HEADING_CHARS ? `${raw.slice(0, MAX_HEADING_CHARS - 1)}…` : raw;
}

const levelOf = (b: ContentBlock): number => Math.max(1, Math.min(9, b.level || 1));

/**
 * The ancestor chain of the block at `index`: walk BACKWARDS collecting the
 * last-seen block at each shallower level, stopping at level 1 (the Part
 * boundary). Blocks the numbering brain gave no prefix — continuations and
 * anything not renderable — are never ancestors: they carry no number of
 * their own, so they cannot name a position.
 */
export function anchorAt(index: number, blocks: ContentBlock[], refs: NumberedRef[]): ModuleAnchor {
  const block = blocks[index];
  if (!block) return { level: 1, path: [], partIndex: 0 };
  const level = levelOf(block);
  const refById = new Map(refs.map((r) => [r.id, r]));

  const path: ModuleAnchorStep[] = [];
  let want = level - 1;
  for (let i = index - 1; i >= 0 && want >= 1; i--) {
    const candidate = blocks[i];
    const candidateLevel = levelOf(candidate);
    if (candidateLevel > want) continue;
    const ref = refById.get(candidate.id);
    if (!ref || !ref.prefix) continue; // continuation / non-printing: no number, no anchor
    path.push({
      level: candidateLevel,
      prefix: ref.prefix,
      headingText: headingTextOf(candidate),
    });
    want = candidateLevel - 1;
  }
  path.reverse(); // Part first → nearest ancestor last

  // counters[0] at this block IS the 1-based Part index the numbering brain
  // was on — no second count, no drift.
  const own = refById.get(block.id);
  const partIndex = own?.counters?.[0] ?? 0;

  return { level, path, partIndex };
}

/** "PART 2 - PRODUCTS › 2.3 Materials" — the human reading of an anchor.
 *  A chunk that starts at a Part reads as that Part itself. */
export function anchorPathLabel(anchor: ModuleAnchor): string {
  if (!anchor.path.length) {
    // Part-only placement (`*.##` — R2 Ruling 6): the part number is the whole
    // address, whatever level the chunk itself starts at.
    return anchor.partIndex > 0 ? `Part ${anchor.partIndex}` : 'Top of the section';
  }
  return anchor.path
    .map((step) => [step.prefix, step.headingText].filter(Boolean).join(' ').trim())
    .join(' › ');
}

// ─── Chunking ───

/**
 * The selected blocks, in DOCUMENT order, grouped into contiguous runs.
 * A gap in the selection starts a new chunk; a single selected block is a
 * chunk of one; a whole Part is one chunk. Ids that are not in `blocks`
 * (deleted under us) are ignored rather than guessed at.
 */
export function buildModuleChunks(
  selectedIds: Iterable<string>,
  blocks: ContentBlock[],
  refs: NumberedRef[],
  mintId: () => string
): ModuleChunk[] {
  const want = new Set(selectedIds);
  const indices: number[] = [];
  for (let i = 0; i < blocks.length; i++) if (want.has(blocks[i].id)) indices.push(i);

  const chunks: ModuleChunk[] = [];
  let run: number[] = [];

  const flush = () => {
    if (!run.length) return;
    chunks.push({
      blocks: run.map((i) => copyBlockForModule(blocks[i], mintId())),
      anchor: anchorAt(run[0], blocks, refs),
    });
    run = [];
  };

  for (const i of indices) {
    if (run.length && i !== run[run.length - 1] + 1) flush();
    run.push(i);
  }
  flush();

  return chunks;
}

/** Blocks captured across every chunk. */
export function moduleBlockCount(module: Pick<SpecModule, 'chunks'>): number {
  return (module.chunks || []).reduce((n, c) => n + (c.blocks?.length || 0), 0);
}

/** "4 blocks · 2 chunks · v3" — the one-line summary a module row has
 *  always carried (pre-R2-categories panel, and the Save-as-module
 *  preview). Kept as ONE pure function so the panel row, the library's
 *  preview and any future surface read identical wording. */
export function moduleCountLabel(module: Pick<SpecModule, 'chunks' | 'version'>): string {
  const blocks = moduleBlockCount(module);
  const chunks = (module.chunks || []).length;
  return `${blocks} block${blocks === 1 ? '' : 's'} · ${chunks} chunk${chunks === 1 ? '' : 's'} · v${module.version ?? 1}`;
}

/** The chunk-anchor preview strip a module row shows: up to `max` anchor
 *  labels ("PART 2 - PRODUCTS › 2.3 Materials"), then a trailing "+N more"
 *  entry for the rest. Empty for a module with no chunks. Pure so the
 *  panel row and any other preview read the same list without re-deriving
 *  the truncation rule by hand. */
export function modulePreviewAnchors(module: Pick<SpecModule, 'chunks'>, max = 2): string[] {
  const chunks = module.chunks || [];
  const shown = chunks.slice(0, max).map((c) => anchorPathLabel(c.anchor));
  if (chunks.length > max) shown.push(`+${chunks.length - max} more`);
  return shown;
}

// ─── MODULE STRUCTURE LAW (R2 Ruling 6, LOCKED) ───
/* Every paragraph in a module lives under a level-2 parent; no orphan
   sub-paragraphs; ≥1 level-2 parent per part the module touches; each level-2
   parent declares its part (1/2/3).

   Only the PART NUMBER matters when the module lands (`*.##` — the star).
   Everything after the star is sequence, and the target spec renumbers in
   place, so a saved article carries `anchor.partIndex` and an EMPTY path:
   `matchAnchor` reads that as "the end of Part N", which is exactly the rule.

   The builder renders the violations below as inline flags and blocks the
   save. This function is the only place the law is written down. */

export const MODULE_PARTS = [1, 2, 3] as const;
export type ModulePart = (typeof MODULE_PARTS)[number];

/** The part a level-2 article declares while it is being edited. */
export const modulePartOf = (block: ContentBlock | undefined): ModulePart | undefined => {
  const raw = (block as ModuleBlock | undefined)?.modulePart;
  return raw === 1 || raw === 2 || raw === 3 ? raw : undefined;
};

/** A level-2 block IS the article — the only thing a module hangs content on. */
export const isModuleArticle = (block: ContentBlock): boolean => levelOf(block) === 2;

export type ModuleViolationKind =
  /** Nothing to save. */
  | 'empty'
  /** A sub-paragraph with no level-2 article above it. */
  | 'orphan'
  /** A level-2 article that has not said which part it belongs to. */
  | 'no-part'
  /** A PART heading with no article under it. */
  | 'part-without-article';

/** One broken rule, named at the block that breaks it. */
export interface ModuleViolation {
  /** The offending block. Empty string only for `empty` (there is no block). */
  blockId: string;
  kind: ModuleViolationKind;
  /** What the flag says on the canvas — short, plain, actionable. */
  message: string;
}

/**
 * The blocks a module builder holds → every way they break Ruling 6.
 * Pure: it reads blocks and writes nothing, so the builder can run it on
 * every keystroke and the tests can run it on a literal.
 */
export function validateModuleStructure(blocks: ContentBlock[]): ModuleViolation[] {
  const list = blocks || [];
  if (!list.length) {
    return [
      { blockId: '', kind: 'empty', message: 'A module needs at least one article to save.' },
    ];
  }

  const out: ModuleViolation[] = [];
  let article: ContentBlock | null = null;
  /** The open PART heading and whether an article has appeared under it yet. */
  let partHeading: ContentBlock | null = null;
  let articleUnderHeading = false;

  const closeHeading = () => {
    if (partHeading && !articleUnderHeading) {
      out.push({
        blockId: partHeading.id,
        kind: 'part-without-article',
        message: 'This part has no article — add one, or delete the heading.',
      });
    }
  };

  for (const block of list) {
    const level = levelOf(block);
    if (level === 1) {
      closeHeading();
      partHeading = block;
      articleUnderHeading = false;
      article = null;
      continue;
    }
    if (level === 2) {
      article = block;
      articleUnderHeading = true;
      if (!modulePartOf(block)) {
        out.push({
          blockId: block.id,
          kind: 'no-part',
          message: 'Choose the part this article belongs to.',
        });
      }
      continue;
    }
    // A paragraph pinned under an article-less part (TWEAKS item 10) sits
    // under that part's header, not under the article above it in the list.
    const pin = (block as ModuleBlock).modulePin;
    if (pin === 1 || pin === 2 || pin === 3) article = null;
    if (!article) {
      out.push({
        blockId: block.id,
        kind: 'orphan',
        message: 'This paragraph has no parent — put an article above it.',
      });
    }
  }
  closeHeading();

  // Every paragraph orphaned and no article anywhere is still "nothing to
  // save" as far as the library is concerned, but the flags above already say
  // where — so the empty case stays reserved for a truly empty canvas.
  return out;
}

/** Nothing broken ⇒ the Save button is live. */
export const moduleStructureIsValid = (blocks: ContentBlock[]): boolean =>
  validateModuleStructure(blocks).length === 0;

/** Violations keyed by block, for a canvas that paints them. */
export function moduleViolationsByBlock(
  violations: ModuleViolation[]
): Record<string, ModuleViolation> {
  const out: Record<string, ModuleViolation> = {};
  for (const v of violations || []) if (v.blockId && !out[v.blockId]) out[v.blockId] = v;
  return out;
}

/** Keys a module block never stores. `modulePart` is builder state (the saved
 *  truth is the anchor); `moduleStamp` belongs to a LANDED copy, never to a
 *  definition — a module that carried one would insert somebody else's
 *  provenance. */
const BUILDER_ONLY_KEYS = ['modulePart', 'modulePin', 'moduleStamp'] as const;

/**
 * Builder canvas → the chunks that get saved.
 *
 * ONE CHUNK PER ARTICLE: a level-2 block opens a chunk and everything under it
 * travels with it, so each chunk lands by its own part number. PART headings
 * are structure the builder shows, never content it saves — the part they stand
 * for is already on the article, and a saved PART heading would insert a second
 * one into the target spec.
 *
 * Block ids are KEPT, not re-minted: they are the definition's own slots, and
 * a landed copy's `sourceBlockId` points at them. (The capture path
 * `buildModuleChunks` still mints, because those ids belong to the source spec.)
 */
export function moduleChunksFromBlocks(blocks: ContentBlock[]): ModuleChunk[] {
  const chunks: ModuleChunk[] = [];
  let current: ModuleChunk | null = null;

  const clean = (block: ContentBlock): ModuleBlock => {
    const copy = deepCopy(block) as unknown as Record<string, unknown>;
    for (const key of RUNTIME_KEYS) delete copy[key];
    for (const key of BUILDER_ONLY_KEYS) delete copy[key];
    return copy as unknown as ModuleBlock;
  };

  for (const block of blocks || []) {
    const level = levelOf(block);
    if (level === 1) {
      current = null;
      continue;
    }
    if (level === 2) {
      current = {
        blocks: [clean(block)],
        anchor: { level: 2, path: [], partIndex: modulePartOf(block) ?? 0 },
      };
      chunks.push(current);
      continue;
    }
    // An orphan cannot be saved (the builder blocks it), so reaching here with
    // no open chunk means the caller ignored the flags: drop it rather than
    // invent a home for it.
    if (current) current.blocks.push(clean(block));
  }

  return chunks;
}

/**
 * The saved chunks → the flat canvas the builder edits, with each article's
 * part read back off its anchor. The inverse of `moduleChunksFromBlocks` for
 * anything the builder itself wrote; a chunk captured the old way (a real
 * ancestor path, any starting level) still opens — it simply arrives with
 * flags on, which is the whole point of putting it in front of a person.
 */
export function moduleBlocksFromChunks(chunks: ModuleChunk[]): ContentBlock[] {
  const out: ContentBlock[] = [];
  for (const chunk of chunks || []) {
    const part = chunk?.anchor?.partIndex;
    let armed = part === 1 || part === 2 || part === 3;
    for (const block of chunk?.blocks || []) {
      const copy = deepCopy(block) as ModuleBlock;
      if (armed && levelOf(copy) === 2) {
        if (!copy.modulePart) copy.modulePart = part;
        armed = false;
      }
      out.push(copy as ContentBlock);
    }
  }
  return out;
}

// ─── The INSERT side (Phase 5) ───

/**
 * What every inserted block carries: WHICH module it came from, at WHICH
 * version, in WHICH insertion.
 *
 * ⛔ Identity, never a link (v2.12 grave). Nothing resolves `moduleId` back to
 * the library to re-read content — the blocks in the spec are the whole truth.
 * `insertId` is minted per INSERT, so dropping the same module twice gives two
 * independent groups that select and remove separately.
 */
export interface ModuleStamp {
  moduleId: string;
  /** The library name AT INSERT TIME, so a landed block can name itself
   *  without a fetch (and still reads honestly if the module is renamed). */
  moduleName: string;
  /** The definition's version at insert time. Phase 6's update PRs diff
   *  against this, so it is the one field that must never be dropped. */
  moduleVersion: number;
  /** This insertion. Unique per drop, shared by every block of it. */
  insertId: string;
  /** TRACEBACK ONLY — never resolved. The id of the chunk block in the
   *  library DEFINITION this landed block was minted from (i.e. the
   *  `ModuleBlock.id` in `chunk.blocks`, before `stampChunkForInsert` mints
   *  this block's own fresh runtime id). Phase 3's per-paragraph update diff
   *  matches on this. OPTIONAL for backward compat: inserts made before this
   *  field existed carry no `sourceBlockId`, and Phase 3 falls back to
   *  whole-module take/keep for those — they still render, list and remove
   *  correctly with nothing else changed. */
  sourceBlockId?: string;
  /** Set by "Keep all mine" (Phase 3 bulk action): the library `version` the
   *  user dismissed an update for. Cleared the moment a NEWER version shows
   *  up, so the offer returns. Absent = never dismissed (also true for every
   *  stamp minted before this field existed). */
  dismissedVersion?: number;
  /** The MODULE'S OWN words for this block at `moduleVersion`, as a
   *  `hashBlock` — the yardstick "does this copy still say what the module
   *  says?" is measured against (R2 Phase 2). Equal to the block's own hash
   *  the moment it lands; re-written whenever the block answers a library
   *  update, so a knowingly-rejected paragraph reads "● modified here".
   *  Derived state, never a mutable flag: the panel re-hashes the block it is
   *  looking at and compares. OPTIONAL for backward compat: an insert made
   *  before this field existed cannot tell intact from modified, and the panel
   *  says so rather than guessing. */
  contentHash?: string;
  /** Module assembly: this chunk's own article heading was dropped because
   *  the spec already had that heading and the chunk merged under it. An
   *  update to the definition must never offer that heading back. */
  mergedHeading?: true;
}

/** A fresh insertion id. */
export const newInsertId = (mintId: () => string = defaultMintId): string => `ins-${mintId()}`;

/** "Gypsum board basis of design v3" — how an insertion names itself. */
export function moduleStampLabel(stamp: ModuleStamp): string {
  return `${stamp.moduleName || 'Module'} v${stamp.moduleVersion ?? 1}`;
}

/**
 * A chunk → the blocks to drop into a target spec.
 *
 * FRESH ids, always: module block ids belong to the definition, and two
 * inserts of one module would otherwise claim the same identity (the editor's
 * `UniqueBlockIds` would re-mint them anyway, behind the stamp's back).
 * `sourceBlockId` rides along untouched — provenance, never resolved.
 */
export function stampChunkForInsert(
  chunk: ModuleChunk,
  stamp: ModuleStamp,
  mintId: () => string = defaultMintId
): ContentBlock[] {
  return (chunk.blocks || []).map((block) => {
    const copy = deepCopy(block) as ModuleBlock;
    // The DEFINITION's block id — the slot Phase 3 matches an incoming
    // library update against — must be captured before we overwrite `id`
    // with this landed block's own fresh runtime id below.
    const sourceBlockId = block.id;
    copy.id = mintId();
    // The content as it LANDED. Hashed here and never again: everything after
    // this compares the block on screen against this number, so "modified in
    // this spec" is derived on read and can never fall out of sync.
    copy.moduleStamp = { ...stamp, sourceBlockId, contentHash: hashBlock(copy) };
    return copy as ContentBlock;
  });
}

/**
 * Stamped blocks → the same blocks with every captured group re-minted
 * through `remap` (one `createGroupRemap` per insert — shared/block-groups).
 * A block whose module carried no group stays ungrouped; a module saved before
 * groups were kept has none at all and simply lands ungrouped. Mutates and
 * returns the array it was given: these are fresh copies from
 * `stampChunkForInsert`, never anybody else's blocks.
 */
export function remapInsertGroups(
  blocks: ContentBlock[],
  remap: (source: string | null | undefined) => string
): ContentBlock[] {
  for (const block of blocks) {
    const next = remap(block.groupId);
    if (next) block.groupId = next;
    else delete block.groupId;
  }
  return blocks;
}

/** The stamp on a block, if it arrived by insertion. */
export const stampOf = (block: ContentBlock): ModuleStamp | null =>
  block && block.moduleStamp && block.moduleStamp.insertId ? block.moduleStamp : null;

// ─── "In this document" (R2 Phase 2) ───

/**
 * Has this insertion been edited since it landed?
 *   · intact   every block still hashes to what it landed as
 *   · modified at least one block's words moved in THIS spec
 *   · unknown  the insert predates `contentHash` — the honest answer is that
 *              we cannot tell, so no intact/modified badge is shown at all
 */
export type ModuleEditState = 'intact' | 'modified' | 'unknown';

/**
 * A `contentHash` no block can ever produce (real ones are 8 hex digits).
 * Written when this copy KNOWINGLY differs from the module at the version it
 * has answered — a paragraph the library dropped that the reader chose to
 * keep. It reads as "● modified here", which is exactly what it is.
 */
export const DEVIATED_HASH = 'deviated';

/** One drop of one module, as it stands in the open document. */
export interface ModuleInsertion {
  insertId: string;
  moduleId: string;
  moduleName: string;
  /** The version every block of this insertion has ANSWERED (the oldest one,
   *  so a copy that is partly behind still hears about the update). */
  moduleVersion: number;
  /** The library version "Keep all mine" dismissed, when EVERY block of the
   *  insertion carries one (the oldest of them, same reasoning). */
  dismissedVersion?: number;
  /** Document order. */
  blockIds: string[];
  blockCount: number;
  /** Every block knows which definition block it came from ⇒ per-paragraph
   *  merge is possible. False on old inserts: whole-module take/keep only. */
  traceable: boolean;
  state: ModuleEditState;
}

/**
 * Every module insertion in a document, in the order they first appear.
 * Blocks group by `insertId` — two drops of the same module are two
 * insertions that select, remove and update independently.
 */
export function listModuleInsertions(blocks: ContentBlock[]): ModuleInsertion[] {
  const out: ModuleInsertion[] = [];
  const byInsert = new Map<string, ModuleInsertion>();
  // The lowest dismissed version across the insertion, and whether EVERY
  // block carried one — a partial dismissal is not a dismissal.
  const dismissed = new Map<string, { lowest: number; all: boolean }>();

  for (const block of blocks || []) {
    const stamp = stampOf(block);
    if (!stamp) continue;
    let insertion = byInsert.get(stamp.insertId);
    if (!insertion) {
      insertion = {
        insertId: stamp.insertId,
        moduleId: stamp.moduleId,
        moduleName: stamp.moduleName || 'Module',
        moduleVersion: stamp.moduleVersion ?? 1,
        blockIds: [],
        blockCount: 0,
        traceable: true,
        state: 'intact',
      };
      byInsert.set(stamp.insertId, insertion);
      dismissed.set(stamp.insertId, { lowest: Number.POSITIVE_INFINITY, all: true });
      out.push(insertion);
    }

    insertion.blockIds.push(block.id);
    insertion.blockCount++;
    insertion.moduleVersion = Math.min(insertion.moduleVersion, stamp.moduleVersion ?? 1);
    if (!stamp.sourceBlockId) insertion.traceable = false;

    const seen = dismissed.get(stamp.insertId);
    if (seen) {
      if (stamp.dismissedVersion == null) seen.all = false;
      else seen.lowest = Math.min(seen.lowest, stamp.dismissedVersion);
    }

    if (!stamp.contentHash) {
      // One un-hashed block makes the whole insertion unreadable — better to
      // say nothing than to call a modified copy intact.
      if (insertion.state !== 'modified') insertion.state = 'unknown';
    } else if (hashBlock(block) !== stamp.contentHash) {
      insertion.state = 'modified';
    }
  }

  for (const insertion of out) {
    const seen = dismissed.get(insertion.insertId);
    if (seen && seen.all && Number.isFinite(seen.lowest)) insertion.dismissedVersion = seen.lowest;
  }
  return out;
}

/** Does the library have something this insertion has not answered? */
export type ModuleUpdateState = 'none' | 'available' | 'dismissed';

export function moduleUpdateState(
  insertion: Pick<ModuleInsertion, 'moduleVersion' | 'dismissedVersion'>,
  libraryVersion: number | undefined
): ModuleUpdateState {
  if (!libraryVersion || libraryVersion <= (insertion.moduleVersion ?? 1)) return 'none';
  // Dismissed AT this version only. A NEWER version no longer matches, so the
  // offer comes back on its own — no flag to clear.
  if (insertion.dismissedVersion != null && insertion.dismissedVersion >= libraryVersion) {
    return 'dismissed';
  }
  return 'available';
}

/** Move part of the stamp on every block of ONE insertion. Pure. */
export function restampInsertion(
  blocks: ContentBlock[],
  insertId: string,
  patch: Partial<ModuleStamp>
): ContentBlock[] {
  return (blocks || []).map((block) => {
    const stamp = stampOf(block);
    if (!stamp || stamp.insertId !== insertId) return block;
    return { ...block, moduleStamp: { ...stamp, ...patch } };
  });
}

// ─── Anchor matching: where does this chunk go in THIS spec? ───

/** Where a chunk lands, or why it cannot land on its own. */
export type AnchorMatch =
  /** One place fits: put the blocks straight after this block. */
  | { kind: 'unique'; insertAfterBlockId: string }
  /** Several places fit (or only part of the path does) — the user picks. */
  | { kind: 'ambiguous'; candidateBlockIds: string[] }
  /** Nothing in this spec resembles where the chunk was standing. */
  | { kind: 'none' };

const norm = (text: string | undefined): string =>
  (text || '').replace(/\s+/g, ' ').trim().toLowerCase();

/** One numbered heading of the target spec, with the chain above it. */
interface OutlineNode {
  index: number;
  id: string;
  level: number;
  prefix: string;
  text: string;
  /** Part first → nearest ancestor last, the same order `ModuleAnchor.path`
   *  uses, so the two can be compared rung for rung. */
  ancestors: OutlineNode[];
}

/**
 * The target's outline. Only blocks the numbering brain gave a PREFIX are
 * rungs — a continuation ("no number") block has no number of its own, so it
 * can neither name a position nor break a subtree, exactly as `anchorAt` says
 * on the capture side.
 */
function outlineOf(blocks: ContentBlock[], refs: NumberedRef[]): OutlineNode[] {
  const refById = new Map(refs.map((r) => [r.id, r]));
  const stack: OutlineNode[] = [];
  const out: OutlineNode[] = [];
  blocks.forEach((block, index) => {
    const ref = refById.get(block.id);
    if (!ref || !ref.prefix) return;
    const level = levelOf(block);
    while (stack.length && stack[stack.length - 1].level >= level) stack.pop();
    const node: OutlineNode = {
      index,
      id: block.id,
      level,
      prefix: ref.prefix,
      text: norm(block.text),
      ancestors: stack.slice(),
    };
    out.push(node);
    stack.push(node);
  });
  return out;
}

/** Does this heading sit at the end of the given ancestor chain? Compared by
 *  LEVEL + TEXT (trimmed, case-insensitive): the number a spec prints moves
 *  the moment an article is added above it, but its words do not. */
function matchesPath(node: OutlineNode, steps: ModuleAnchorStep[]): boolean {
  const chain = [...node.ancestors, node];
  if (chain.length < steps.length) return false;
  const tail = chain.slice(chain.length - steps.length);
  return steps.every((s, i) => tail[i].level === s.level && tail[i].text === norm(s.headingText));
}

/**
 * The LAST block that belongs to the heading at `index` — its own subtree,
 * ending where the next same-or-shallower NUMBERED heading begins. That is
 * where a chunk captured under that heading goes: after everything already
 * there, never in front of it.
 */
export function endOfSubtree(
  index: number,
  level: number,
  blocks: ContentBlock[],
  refs: NumberedRef[]
): number {
  const refById = new Map(refs.map((r) => [r.id, r]));
  let last = index;
  for (let i = index + 1; i < blocks.length; i++) {
    const ref = refById.get(blocks[i].id);
    // A block with no printed number is a continuation of what came before it,
    // so it travels with the subtree instead of ending it.
    if (ref && ref.prefix && levelOf(blocks[i]) <= level) break;
    last = i;
  }
  return last;
}

/**
 * Where does this chunk go in THIS spec?
 *
 * Walk the chunk's ancestor chain against the target's outline: every rung has
 * to match by level and heading TEXT, with the printed prefix used only to
 * break a tie. One full-path match places the chunk at the END of that
 * ancestor's subtree. Several matches — or a match on only the shallower part
 * of the chain — is AMBIGUOUS: the user places it by hand rather than the app
 * guessing. Nothing recognisable is NONE.
 */
export function matchAnchor(
  anchor: ModuleAnchor,
  blocks: ContentBlock[],
  refs: NumberedRef[]
): AnchorMatch {
  const outline = outlineOf(blocks, refs);
  const landing = (node: OutlineNode): AnchorMatch => ({
    kind: 'unique',
    insertAfterBlockId: blocks[endOfSubtree(node.index, node.level, blocks, refs)].id,
  });

  // A chunk captured AT Part level has no ancestors to match — the Part index
  // it was standing in is the only thing it knows about itself.
  if (!anchor.path.length) {
    const parts = outline.filter((n) => n.level === 1);
    const part = anchor.partIndex > 0 ? parts[anchor.partIndex - 1] : undefined;
    return part ? landing(part) : { kind: 'none' };
  }

  for (let take = anchor.path.length; take >= 1; take--) {
    const steps = anchor.path.slice(0, take);
    let hits = outline.filter((n) => matchesPath(n, steps));
    if (!hits.length) continue;
    if (hits.length > 1) {
      // Same words in two places: the number the source printed decides.
      const exact = hits.filter((n) => n.prefix === steps[steps.length - 1].prefix);
      if (exact.length === 1) hits = exact;
    }
    if (take === anchor.path.length && hits.length === 1) return landing(hits[0]);
    return { kind: 'ambiguous', candidateBlockIds: hits.map((n) => n.id) };
  }

  return { kind: 'none' };
}

// ─── The definition ───

export interface BuildModuleInput {
  name: string;
  description?: string;
  selectedIds: Iterable<string>;
  blocks: ContentBlock[];
  refs: NumberedRef[];
  sourceSpecId?: string;
  sourceCsi?: string;
  userName?: string;
  /** Injected so a test (and only a test) can be deterministic. */
  mintId?: () => string;
  /** Injected for the same reason. */
  now?: () => string;
}

/**
 * Selection → a version-1 module definition, ready to save. Pure: it reads
 * the spec and writes nothing back, so "save as module" can never mutate the
 * spec it was captured from.
 */
export function buildModuleDefinition(input: BuildModuleInput): Omit<SpecModule, 'id'> & {
  id: string;
} {
  const mintId = input.mintId || defaultMintId;
  const nowIso = (input.now || (() => new Date().toISOString()))();
  const chunks = buildModuleChunks(input.selectedIds, input.blocks, input.refs, mintId);

  return {
    id: mintId(),
    name: input.name.trim(),
    description: input.description?.trim() || undefined,
    chunks,
    version: 1,
    sourceSpecId: input.sourceSpecId,
    sourceCsi: input.sourceCsi,
    archivedAt: null,
    createdBy: input.userName,
    createdAt: nowIso,
    modifiedBy: input.userName,
    lastModified: nowIso,
  };
}

// ─── The UPDATE side (Phase 6) ───

/* ⛔ IDENTITY RULING (Phase 6, binding — the thing to remember about this
   half of the file).

   A definition is RE-CAPTURED from a live selection every time it is
   updated, and `copyBlockForModule` mints a fresh id for every block it
   copies. So the ids in v2 of a module have nothing to do with the ids in
   v1: they cannot name a slot across two captures.

   The identity of a definition slot is therefore its POSITION —
   (chunk index, ordinal within that chunk). Read that way:

     · same position, different content   → `modified`
     · position only the new capture has  → `added`
     · position only the old capture has  → `removed`
     · two blocks that swapped places     → TWO `modified` ops, never a move

   The last line is the honest cost of a positional identity and it is what
   the update cards show. Chunks pair by index for the same reason. */

export type ModuleDefOpKind = 'modified' | 'added' | 'removed';

/** One change to the DEFINITION, at one slot. */
export interface ModuleDefOp {
  opId: string;
  chunkIndex: number;
  /** Ordinal within that chunk — see the identity ruling above. */
  ordinal: number;
  kind: ModuleDefOpKind;
  /** The definition block as the OLD version had it. Absent on 'added'. */
  before?: ModuleBlock;
  /** The definition block as the NEW version has it. Absent on 'removed'. */
  after?: ModuleBlock;
}

/**
 * Everything an update PR needs to know about the definition change.
 * `oldChunks` travels with the ops on purpose: mapping an op onto a copy that
 * has drifted needs the WHOLE old ladder, not just the rungs that moved, and
 * the server re-runs that mapping from the stored row.
 */
export interface ModuleDefDiff {
  /** The OLD definition's blocks, per chunk, in ordinal order. */
  oldChunks: ModuleBlock[][];
  ops: ModuleDefOp[];
}

/**
 * Old definition vs new definition → the ops. Pure, and the ONLY place the
 * positional identity above is implemented.
 *
 * Content is compared with the compose brain's `hashBlock` — the same
 * yardstick that decides whether a client override has gone stale, so a
 * definition can never be "changed" here and "unchanged" there.
 */
export function diffModuleDefinition(
  oldChunks: ModuleChunk[],
  newChunks: ModuleChunk[],
  mintId: () => string = defaultMintId
): ModuleDefDiff {
  const olds = (oldChunks || []).map((c) => c?.blocks || []);
  const news = (newChunks || []).map((c) => c?.blocks || []);
  const ops: ModuleDefOp[] = [];

  const chunkCount = Math.max(olds.length, news.length);
  for (let chunkIndex = 0; chunkIndex < chunkCount; chunkIndex++) {
    const before = olds[chunkIndex] || [];
    const after = news[chunkIndex] || [];
    const slots = Math.max(before.length, after.length);
    for (let ordinal = 0; ordinal < slots; ordinal++) {
      const b = before[ordinal];
      const a = after[ordinal];
      const base = { opId: `mop-${mintId()}`, chunkIndex, ordinal };
      if (b && a) {
        if (hashBlock(b) !== hashBlock(a)) {
          ops.push({ ...base, kind: 'modified', before: b, after: a });
        }
        continue;
      }
      if (a) ops.push({ ...base, kind: 'added', after: a });
      else if (b) ops.push({ ...base, kind: 'removed', before: b });
    }
  }

  return { oldChunks: olds, ops };
}

/** Did anything actually move? */
export const defDiffIsEmpty = (diff: ModuleDefDiff | undefined): boolean =>
  !diff || !(diff.ops || []).length;

/** One op, read against ONE insertion in ONE document. */
export interface MappedDefOp {
  op: ModuleDefOp;
  /** The insertion (one drop of the module) this mapping belongs to. */
  insertId: string;
  /** The block in THIS copy the op acts on — modified / removed. */
  instanceBlockId?: string;
  /** For 'added': the block the new one goes after… */
  afterBlockId?: string;
  /** …or, when nothing precedes it survived, the block it goes before. */
  beforeBlockId?: string;
  /** The op cannot land: the block it names was deleted from this copy (or,
   *  for an add, everything around it was). Recorded, never applied. */
  unmappable?: boolean;
}

/** Alignment is O(n·m); a module this size is already pathological, and a
 *  positional fallback is better than a stalled request. */
const MAX_ALIGN_CELLS = 160000;

/** Matched (defIndex, instanceIndex) pairs — the untouched blocks, which are
 *  the anchors everything else is measured from. Plain LCS over content
 *  hashes. Exported for the LOCAL update (R2 Phase 3), which falls back to
 *  this same alignment for any slot the traceback cannot name. */
export function lcsPairs(a: string[], b: string[]): Array<[number, number]> {
  const m = a.length;
  const n = b.length;
  if (!m || !n || m * n > MAX_ALIGN_CELLS) return [];
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const pairs: Array<[number, number]> = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      pairs.unshift([i - 1, j - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) i--;
    else j--;
  }
  return pairs;
}

/**
 * Definition slot → the block in this copy that stands for it.
 *
 * ANCHORS then GAPS: blocks whose content still matches the definition are
 * matched exactly (LCS), and whatever sits between two anchors is paired off
 * in order. That is what makes a DEVIATED copy map correctly — a block whose
 * words were edited locally sits in a gap of one, between two untouched
 * neighbours, and pairs with its slot — while a block the user DELETED leaves
 * its slot with nothing to pair with, which is exactly the "can't apply" case.
 * Extra local blocks inside an insertion are ignored: they are the user's, not
 * the module's.
 */
function alignDefToInstance(defBlocks: ContentBlock[], inst: ContentBlock[]): Array<string | null> {
  const out: Array<string | null> = new Array(defBlocks.length).fill(null);
  const pairs = lcsPairs(
    defBlocks.map((b) => hashBlock(b)),
    inst.map((b) => hashBlock(b))
  );

  let di = 0;
  let ii = 0;
  const fillGap = (dEnd: number, iEnd: number): void => {
    while (di < dEnd && ii < iEnd) {
      out[di] = inst[ii].id;
      di++;
      ii++;
    }
    di = dEnd;
    ii = iEnd;
  };

  for (const [d, i] of pairs) {
    fillGap(d, i);
    out[d] = inst[i].id;
    di = d + 1;
    ii = i + 1;
  }
  fillGap(defBlocks.length, inst.length);
  return out;
}

/**
 * The definition's ops → what they mean for ONE document.
 *
 * Every insertion of the module is mapped SEPARATELY (two drops of the same
 * module in one spec are two independent groups — that is what `insertId` is
 * for), so the same op can land twice, once per insertion, and answering one
 * never touches the other.
 */
export function mapDefOpsToInstance(
  diff: ModuleDefDiff,
  instanceBlocks: ContentBlock[],
  moduleId: string
): MappedDefOp[] {
  const oldChunks = (diff?.oldChunks || []).map((blocks) => blocks || []);
  const flat: ModuleBlock[] = [];
  const slotAt = new Map<string, number>();
  oldChunks.forEach((blocks, chunkIndex) => {
    blocks.forEach((block, ordinal) => {
      slotAt.set(`${chunkIndex}:${ordinal}`, flat.length);
      flat.push(block);
    });
  });

  // The insertions of THIS module, each in document order.
  const groups = new Map<string, ContentBlock[]>();
  for (const block of instanceBlocks || []) {
    const stamp = stampOf(block);
    if (!stamp || stamp.moduleId !== moduleId) continue;
    const list = groups.get(stamp.insertId);
    if (list) list.push(block);
    else groups.set(stamp.insertId, [block]);
  }

  const out: MappedDefOp[] = [];
  for (const [insertId, blocks] of groups) {
    const mapped = alignDefToInstance(flat, blocks);
    const mappedIds = mapped.filter((id): id is string => !!id);
    const lastMappedId = mappedIds.length ? mappedIds[mappedIds.length - 1] : undefined;

    const idOfSlot = (chunkIndex: number, ordinal: number): string | null => {
      const at = slotAt.get(`${chunkIndex}:${ordinal}`);
      return at == null ? null : mapped[at];
    };

    for (const op of diff?.ops || []) {
      if (op.kind !== 'added') {
        const instanceBlockId = idOfSlot(op.chunkIndex, op.ordinal);
        out.push(
          instanceBlockId ? { op, insertId, instanceBlockId } : { op, insertId, unmappable: true }
        );
        continue;
      }

      // An ADD lands next to its neighbours in the same chunk: after the
      // nearest earlier slot that survived, else before the nearest later one.
      let afterBlockId: string | null = null;
      for (let o = op.ordinal - 1; o >= 0 && !afterBlockId; o--) {
        afterBlockId = idOfSlot(op.chunkIndex, o);
      }
      let beforeBlockId: string | null = null;
      if (!afterBlockId) {
        const chunkLen = oldChunks[op.chunkIndex]?.length || 0;
        for (let o = op.ordinal; o < chunkLen && !beforeBlockId; o++) {
          beforeBlockId = idOfSlot(op.chunkIndex, o);
        }
      }
      // A chunk the old definition never had (or one wiped out locally) has no
      // neighbours of its own: the end of this insertion is the honest spot.
      if (!afterBlockId && !beforeBlockId && lastMappedId) afterBlockId = lastMappedId;

      out.push(
        afterBlockId || beforeBlockId
          ? {
              op,
              insertId,
              ...(afterBlockId ? { afterBlockId } : {}),
              ...(beforeBlockId ? { beforeBlockId } : {}),
            }
          : { op, insertId, unmappable: true }
      );
    }
  }
  return out;
}

// ─── Dragging a module onto the document (modules-polish, item 3) ───

/** The HTML5 drag type a Modules-panel row carries (value = the module id).
 *  Deliberately the ONLY type on the drag: no text/plain or text/html, so
 *  ProseMirror's own drop handler finds nothing to paste and the drop is
 *  ours alone (useModuleInsert). */
export const MODULE_DRAG_MIME = 'application/x-sf-module';

/** The Parts a module has chunks for, ascending, de-duplicated. */
export function moduleChunkParts(chunks: ModuleChunk[]): number[] {
  const parts = new Set<number>();
  for (const chunk of chunks || []) parts.add(chunk?.anchor?.partIndex ?? 0);
  return [...parts].sort((a, b) => a - b);
}

/** "Part 2 and Part 3" — how a hint or a toast names a set of Parts. */
export function partListLabel(parts: number[]): string {
  const names = parts.map((p) => (p > 0 ? `Part ${p}` : 'the top of the section'));
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The Part a drop BOUNDARY sits in — the Part of the block just above the
 * line (the gap before block `index`). A line right above "PART 2" is the
 * END of Part 1, which is exactly what a person aiming there means; a line
 * above everything is 0, "before any Part". Read off the numbering brain's own
 * counters, the same number `anchorAt` recorded at capture.
 */
export function partAtBoundary(blocks: ContentBlock[], refs: NumberedRef[], index: number): number {
  if (index <= 0 || !blocks.length) return 0;
  const above = blocks[Math.min(index, blocks.length) - 1];
  const ref = refs.find((r) => r.id === above.id);
  return ref?.counters?.[0] ?? 0;
}

/** How a module splits when it is dropped onto Part `part`. */
export interface ModuleDropSplit {
  /** The chunks that follow the pointer, in module order. */
  here: ModuleChunk[];
  /** Every other chunk — placed by `matchAnchor`, exactly as Insert does. */
  elsewhere: ModuleChunk[];
  /** Every Part the module has chunks for (the "belongs in" hint). */
  parts: number[];
  /** False ⇒ the module has nothing for this Part: no line, no drop. */
  allowed: boolean;
}

/** Pure: which chunks land at the line, which place themselves. */
export function splitModuleForDrop(chunks: ModuleChunk[], part: number): ModuleDropSplit {
  const here: ModuleChunk[] = [];
  const elsewhere: ModuleChunk[] = [];
  for (const chunk of chunks || []) {
    if (!chunk?.blocks?.length) continue;
    if ((chunk.anchor?.partIndex ?? 0) === part) here.push(chunk);
    else elsewhere.push(chunk);
  }
  return { here, elsewhere, parts: moduleChunkParts(chunks), allowed: here.length > 0 };
}

// ─── Telling insertions apart (modules-polish, item 4) ───

/**
 * One colour SLOT (0…slots-1) per insertion, given the document's top-level
 * blocks in order as their insertId (or null for a block that is no module's).
 *
 *   · STABLE: an insertion prefers the slot its own id hashes to, so the same
 *     insertion keeps its colour across reloads and while unrelated insertions
 *     come and go elsewhere;
 *   · DISTINCT: two insertions whose runs TOUCH never share a slot — the
 *     preferred slot is only given up to a touching neighbour already coloured.
 *
 * Colour is never the only cue (DESIGN §7): every run also gets a name tag.
 */
export function insertionColorSlots(seq: Array<string | null>, slots = 4): Map<string, number> {
  const touching = new Map<string, Set<string>>();
  const order: string[] = [];
  const link = (a: string, b: string) => {
    if (!touching.has(a)) touching.set(a, new Set());
    touching.get(a)!.add(b);
  };
  for (let i = 0; i < seq.length; i++) {
    const id = seq[i];
    if (!id) continue;
    if (!touching.has(id)) {
      touching.set(id, new Set());
      order.push(id);
    }
    const prev = i > 0 ? seq[i - 1] : null;
    if (prev && prev !== id) {
      link(id, prev);
      link(prev, id);
    }
  }
  const out = new Map<string, number>();
  for (const id of order) {
    const taken = new Set<number>();
    for (const n of touching.get(id) || []) {
      const slot = out.get(n);
      if (slot != null) taken.add(slot);
    }
    const preferred = hashSlot(id, slots);
    let slot = preferred;
    for (let k = 0; k < slots; k++) {
      const candidate = (preferred + k) % slots;
      if (!taken.has(candidate)) {
        slot = candidate;
        break;
      }
    }
    out.set(id, slot);
  }
  return out;
}

function hashSlot(id: string, slots: number): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % slots;
}
